import type { ClientUser } from "api-types/users/usersResponses";
import { FALLBACK_VALUE } from "common/constants";
import getUserFullName, { constructUserName } from "lib/utils/userName";
import cx from "ui/cx";
import styles from "./Avatar.module.scss";

export type AvatarSize = "medium" | "small";

export const AVATAR_ID = "HIGHARC_AVATAR";

interface AvatarProps extends React.HTMLProps<HTMLDivElement> {
  user: Pick<ClientUser, "firstName" | "lastName"> | null;
  displayCount?: number;
  avatarSize?: AvatarSize;
  avatarStyle?: "pending";
  showName?: boolean;
}

/**
 * Can display an abbreviation of a user name (e.g. John Doe becomes JD), or an arbitrary number
 * to represent a count.
 */
export default function Avatar({
  user,
  displayCount,
  onClick,
  avatarSize,
  avatarStyle,
  showName = false,
  className,
  ...props
}: AvatarProps) {
  const fallbackUser = constructUserName(FALLBACK_VALUE);
  const firstChar = user?.firstName ? user.firstName.charAt(0) : fallbackUser.firstName.charAt(0);
  const lastChar = user?.lastName ? user.lastName.charAt(0) : fallbackUser.lastName.charAt(0);
  const userAbbr = firstChar + lastChar;

  const bubbleClassNames = cx(
    {
      styles,
      wrapper: true,
      [avatarSize ?? ""]: true,
      [avatarStyle ?? ""]: true,
      clickable: !!onClick,
    },
    className,
  );

  const nameClassNames = cx({
    styles,
    avatarName: true,
    [avatarSize ?? ""]: true,
    [avatarStyle ?? ""]: true,
  });

  const avatarBubble = (
    <div id={AVATAR_ID} className={bubbleClassNames} onClick={onClick} {...props}>
      {displayCount ?? userAbbr}
    </div>
  );

  return showName ? (
    <div className={styles.avatarContainer}>
      {avatarBubble}
      <span className={nameClassNames}>{getUserFullName(user)}</span>
    </div>
  ) : (
    avatarBubble
  );
}
